import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { ArrowLeft, Compass, Moon, Target, Zap, Trophy, BookOpen, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const WeeklyJournal = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t } = useTranslation();
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWeek, setExpandedWeek] = useState(null);

  useEffect(() => { fetchJournal(); }, []);

  const fetchJournal = async () => {
    try {
      const res = await api.get('/api/journal/weeks');
      setWeeks(res.data.weeks || []);
    } catch (err) { console.error('Journal fetch error:', err); }
    finally { setLoading(false); }
  };

  const getWeekDates = (weekKey) => {
    // Parse YYYY-WNN and compute Monday-Sunday dates
    try {
      const [yearStr, wStr] = weekKey.split('-W');
      const year = parseInt(yearStr);
      const week = parseInt(wStr);
      const jan4 = new Date(year, 0, 4);
      const dayOfWeek = jan4.getDay() || 7;
      const monday = new Date(jan4);
      monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const fmt = (d) => `${d.getDate()}/${d.getMonth() + 1}`;
      return `${fmt(monday)} — ${fmt(sunday)}`;
    } catch { return weekKey; }
  };

  const getWeekSummary = (week) => {
    const parts = [];
    const b = week.boussole;
    const d = week.debrief;
    if (b && b.statut === 'completee') {
      const pCount = b.priorites?.length || 0;
      if (pCount > 0) parts.push(`${pCount} ${t('journal.prioritiesSet')}`);
      if (b.decision_attendue) parts.push(`1 ${t('journal.decisionMade')}`);
    }
    if (d && d.statut === 'completee') {
      if (d.victoire) parts.push(`1 ${t('journal.victory')}`);
    }
    return parts.join(' · ') || t('journal.noActivity');
  };

  const toggleExpand = (weekKey) => {
    setExpandedWeek(prev => prev === weekKey ? null : weekKey);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-atlasCyan animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div data-testid="weekly-journal-page" className="p-6 lg:p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <button onClick={() => navigate('/profile')} data-testid="journal-back-btn" className="p-2 rounded-lg text-atlasTextSecondary hover:text-white hover:bg-white/5 transition-all">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">{t('journal.title')}</h1>
          <p className="text-sm text-atlasTextSecondary">{t('journal.subtitle')}</p>
        </div>
      </div>

      {weeks.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <p className="text-atlasTextSecondary">{t('journal.empty')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {weeks.map((week) => {
            const isExpanded = expandedWeek === week.semaine;
            const b = week.boussole;
            const d = week.debrief;
            return (
              <div key={week.semaine} data-testid={`journal-week-${week.semaine}`} className="glass-card overflow-hidden transition-all">
                {/* Week header */}
                <button onClick={() => toggleExpand(week.semaine)} data-testid={`journal-toggle-${week.semaine}`}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-colors">
                  <div>
                    <p className="text-white font-semibold">{t('journal.weekOf')} {getWeekDates(week.semaine)}</p>
                    <p className="text-xs text-atlasTextSecondary mt-1">{getWeekSummary(week)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {b && b.statut === 'completee' && <Compass className="w-4 h-4 text-atlasCyan" />}
                    {d && d.statut === 'completee' && <Moon className="w-4 h-4 text-atlasPurple" />}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-atlasTextSecondary" /> : <ChevronDown className="w-4 h-4 text-atlasTextSecondary" />}
                  </div>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="px-5 pb-5 space-y-4">
                    {/* Boussole */}
                    {b && b.statut === 'completee' && (
                      <div className="rounded-xl border border-atlasCyan/20 bg-atlasCyan/[0.03] p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Compass className="w-4 h-4 text-atlasCyan" />
                          <span className="text-sm font-medium text-atlasCyan">{t('boussole.title')}</span>
                        </div>
                        {b.emotion_note && (
                          <p className="text-xs text-atlasTextSecondary">{t('journal.emotion')}: {b.emotion_note}/10 — {b.emotion_mot}</p>
                        )}
                        {b.kpi_cle && (
                          <p className="text-xs text-atlasTextSecondary">{t('journal.kpi')}: {b.kpi_cle.label} ({b.kpi_cle.objectif})</p>
                        )}
                        {b.priorites?.length > 0 && (
                          <div>
                            <p className="text-xs text-atlasTextSecondary mb-1"><Target className="w-3 h-3 inline mr-1" />{t('journal.priorities')}</p>
                            <ul className="space-y-1">
                              {b.priorites.map((p, i) => (
                                <li key={i} className="text-sm text-white flex items-start gap-2">
                                  <span className="text-atlasCyan text-xs font-bold mt-0.5">#{i + 1}</span>
                                  <span>{p}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {b.decision_attendue && (
                          <p className="text-xs text-atlasTextSecondary"><Zap className="w-3 h-3 inline mr-1" />{t('journal.decision')}: <span className="text-white">{b.decision_attendue.label}</span></p>
                        )}
                      </div>
                    )}
                    {b && b.statut === 'skippee' && (
                      <p className="text-xs text-atlasTextSecondary italic">{t('journal.boussoleSkipped')}</p>
                    )}

                    {/* Debrief */}
                    {d && d.statut === 'completee' && (
                      <div className="rounded-xl border border-atlasPurple/20 bg-atlasPurple/[0.03] p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Moon className="w-4 h-4 text-atlasPurple" />
                          <span className="text-sm font-medium text-atlasPurple">{t('debrief.title')}</span>
                        </div>
                        {d.victoire && (
                          <p className="text-xs text-atlasTextSecondary"><Trophy className="w-3 h-3 inline mr-1" />{t('journal.victoryLabel')}: <span className="text-white">{d.victoire}</span></p>
                        )}
                        {d.apprentissage && (
                          <p className="text-xs text-atlasTextSecondary"><BookOpen className="w-3 h-3 inline mr-1" />{t('journal.learningLabel')}: <span className="text-white">{d.apprentissage}</span></p>
                        )}
                        {d.sujet_ouvert_weekend && (
                          <p className="text-xs text-atlasTextSecondary"><AlertCircle className="w-3 h-3 inline mr-1" />{t('journal.openTopicLabel')}: <span className="text-white">{d.sujet_ouvert_weekend}</span></p>
                        )}
                      </div>
                    )}
                    {d && d.statut === 'skippee' && (
                      <p className="text-xs text-atlasTextSecondary italic">{t('journal.debriefSkipped')}</p>
                    )}

                    {!b && !d && (
                      <p className="text-xs text-atlasTextSecondary italic">{t('journal.noActivity')}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WeeklyJournal;
